import React, { createContext, useState, useContext } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

const DownloadContext = createContext();

export const useDownload = () => useContext(DownloadContext);

export const DownloadProvider = ({ children }) => {
  const [videoInfo, setVideoInfo] = useState(null);
  const [playlistInfo, setPlaylistInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloadHistory, setDownloadHistory] = useState([]);
  const [supportedPlatforms, setSupportedPlatforms] = useState([]);
  const [downloadProgress, setDownloadProgress] = useState({});

  // Fetch supported platforms
  const fetchSupportedPlatforms = async () => {
    try {
      const response = await axios.get(`${API_URL}/platforms`);
      setSupportedPlatforms(response.data.data);
      return response.data.data;
    } catch (error) {
      console.error('Error fetching platforms:', error);
      return [];
    }
  };

  // Universal URL info fetcher (works for any platform)
  const fetchMediaInfo = async (url, type = 'video') => {
    setLoading(true);
    try {
      const endpoint = type === 'playlist' ? '/playlist/info' : '/media/info';
      const response = await axios.post(`${API_URL}${endpoint}`, { url });
      
      if (type === 'playlist') {
        setPlaylistInfo(response.data.data);
      } else {
        setVideoInfo(response.data.data);
      }
      
      toast.success('Media info fetched successfully');
      return response.data.data;
    } catch (error) {
      console.error('Error fetching media info:', error);
      toast.error(error.response?.data?.message || 'Failed to fetch media info');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Universal download function with progress tracking
  const downloadMedia = async (mediaId, options) => {
    const { format, quality, platform = 'youtube', includeSubtitles = false } = options;
    
    try {
      const params = new URLSearchParams({
        format,
        quality,
        platform,
        ...(includeSubtitles && { subtitles: 'en' })
      });

      const downloadUrl = `${API_URL}/media/${mediaId}/download?${params.toString()}`;
      
      // Track download in history
      const downloadItem = {
        id: Date.now(),
        mediaId,
        url: downloadUrl,
        format,
        quality,
        platform,
        timestamp: new Date().toISOString(),
        status: 'started'
      };
      
      setDownloadHistory(prev => [downloadItem, ...prev]);

      // For single file download
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = '';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Update history
      setDownloadHistory(prev => 
        prev.map(item => 
          item.id === downloadItem.id 
            ? { ...item, status: 'completed' } 
            : item
        )
      );

      toast.success('Download started!');
      return downloadItem;
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Download failed');
      throw error;
    }
  };

  // Batch download for playlists/multiple files
  const batchDownload = async (items, options) => {
    const total = items.length;
    let completed = 0;

    for (const item of items) {
      try {
        await downloadMedia(item.id, options);
        completed++;
        setDownloadProgress({
          total,
          completed,
          percentage: (completed / total) * 100
        });
      } catch (error) {
        console.error(`Failed to download ${item.id}:`, error);
      }
    }

    toast.success(`Downloaded ${completed} of ${total} items`);
  };

  // Get download history
  const getDownloadHistory = () => downloadHistory;

  // Clear history
  const clearHistory = () => {
    setDownloadHistory([]);
    toast.success('History cleared');
  };

  // Enhanced playlist download with multiple format support
  const downloadPlaylistItems = async (playlistId, selectedVideos, options) => {
    try {
      const response = await axios.post(`${API_URL}/playlist/${playlistId}/batch-download`, {
        videos: selectedVideos,
        options
      });

      // If server returns a zip file
      if (response.data.zipUrl) {
        const link = document.createElement('a');
        link.href = response.data.zipUrl;
        link.download = `playlist-${playlistId}.zip`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }

      toast.success('Playlist download started');
      return response.data;
    } catch (error) {
      console.error('Playlist download error:', error);
      toast.error('Failed to download playlist');
      throw error;
    }
  };

  const value = {
    videoInfo,
    playlistInfo,
    loading,
    downloadProgress,
    downloadHistory,
    supportedPlatforms,
    fetchSupportedPlatforms,
    fetchMediaInfo,
    downloadMedia,
    batchDownload,
    downloadPlaylistItems,
    getDownloadHistory,
    clearHistory,
    setVideoInfo,
    setPlaylistInfo
  };

  return (
    <DownloadContext.Provider value={value}>
      {children}
    </DownloadContext.Provider>
  );
};